// "Linking reactions" — the load-takedown graph: why reactions link instead
// of being retyped, the picker end-to-end, the full refusal table (the
// picker's verbatim on-screen sentences), gravity-only connection links, and
// the staleness lifecycle. Fixture numbers are imported from the same module
// the figure captures render (calcPreviewFixtures).
import type { HelpArticle } from "../../types";
import {
  CFS_JOIST_REACTIONS,
  CONCRETE_REACTION_REFUSAL,
  LINK_B1_FJ1,
  RAFTER_PACKET,
  lineTransferPlf,
} from "../../../calculations/calcPreviewFixtures";

const grp = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
const plf = (lb: number): string => grp(lineTransferPlf(lb, LINK_B1_FJ1.spacingIn));

export const linkingReactionsArticle: HelpArticle = {
  id: "linking-reactions",
  title: "Linking reactions",
  description:
    "Carry one member's support reaction onto another as a live, per-load-type packet: the picker, the landing types, every refusal reason verbatim, and the staleness lifecycle.",
  tags: ["load link", "reaction", "packet", "stale", "recompute", "load path"],
  lastUpdated: "July 19, 2026",
  published: true,
  blocks: [
    {
      kind: "lead",
      text: "When a beam carries a joist, the joist's demand is the beam's reaction. Firmark carries that number as a live edge between the two sheets, so nobody retypes it and nothing drifts silently. This article is the whole feature: creating a link, what the packet transfers, every reason the picker refuses, and what happens when the source changes.",
    },

    { kind: "heading", level: 2, text: "Why link instead of retype", id: "why-link" },
    {
      kind: "paragraph",
      text: "A retyped reaction is a snapshot with no memory of where it came from. A link is an edge in the project's load-takedown graph, and it buys three properties a snapshot cannot:",
    },
    {
      kind: "list",
      style: "unordered",
      items: [
        "**Live.** The engine re-reads the source's authoritative reaction at every recomputation of the target. Change the beam's span and the joist load it carries follows at the next recompute — and every affected sheet is flagged OUT OF DATE until then, so nothing moves silently.",
        "**Per-ASCE-type, unfactored.** The packet carries D, L, Lr, S, R, Di, W_down, and W_up as separate service-level currencies. The target runs its own load combinations and duration factors on them; a single pre-factored number could never survive a CD change or an LRFD override downstream.",
        "**Rename-safe.** The edge is stored against the source's immutable sheet id, not its mark. Rename `B-1` to `G-1` and every link that reads it holds.",
      ],
    },
    {
      kind: "values",
      caption: "The demo edge this article follows: B-1's left support onto FJ-1.",
      rows: [
        { label: "Source packet (B-1, left)", value: `D ${grp(LINK_B1_FJ1.byType.D)} lb · L ${grp(LINK_B1_FJ1.byType.L)} lb (service, unfactored)` },
        { label: "Landing", value: `line load at ${LINK_B1_FJ1.spacingIn} in o.c. spacing` },
        { label: "FJ-1 receives", value: `D ${plf(LINK_B1_FJ1.byType.D)} plf · L ${plf(LINK_B1_FJ1.byType.L)} plf` },
      ],
    },
    {
      kind: "image",
      src: "/help-figures/reaction-packet-flow.svg",
      alt: "Reaction packet flow: B-1 computes support reactions, publishes an authoritative per-load-type packet from its left support, and FJ-1 consumes it as a read-only line-load row that re-resolves at every recompute.",
      caption: "The path of one reaction: source supports → typed unfactored packet → the target's inherited load row. The target combines under its own design method.",
    },

    { kind: "heading", level: 2, text: "Creating a link", id: "creating-a-link" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", "FJ-1", "B. Loads"] },
    {
      kind: "steps",
      steps: [
        {
          title: "Open the picker",
          body: "**Link reaction** sits in card B's header on member sheets; connection sheets carry **Link a member reaction** in their demand step. One fetch loads every member's current reactions — the picker is built for hundreds of sheets, so there is a search box and members group by load-path role.",
        },
        {
          title: "Pick a source reaction",
          body: "**Likely sources** floats members one tier up the load path (joists above beams, beams above posts) to the top. Each member row shows one chip per support: the support name, the maximum envelope when one is published, the active load-type letters, and `cases` / `moment` markers when the packet carries correlated cases or fixed-end moments. Chips that cannot land on this target are disabled and say why.",
        },
        {
          title: "Choose how it lands",
          body: "The target calculator decides which landings are offered: **Line load** (divides by the spacing you enter, in plf), **Point load** (at a position in ft from the left support), **Axial load** for columns, studs, and ties. Connection targets skip the choice — their mapping is fixed and shown for confirmation instead.",
        },
        {
          title: "Read the transfer preview, then Add link",
          body: `The preview prints the exact per-type values the target will receive before you commit. B-1's left reaction at ${LINK_B1_FJ1.spacingIn} in spacing: D ${grp(LINK_B1_FJ1.byType.D)} lb becomes ${plf(LINK_B1_FJ1.byType.D)} plf, L ${grp(LINK_B1_FJ1.byType.L)} lb becomes ${plf(LINK_B1_FJ1.byType.L)} plf. Confirming creates the edge and recomputes the target immediately.`,
        },
      ],
    },
    {
      kind: "shot",
      shotId: "link-transfer-preview",
      caption: "Landing B-1's left reaction on FJ-1 as a line load: per-type transfer, exact values, before anything commits.",
    },
    {
      kind: "paragraph",
      text: "The linked load then appears in card B under **Inherited from linked members**: a read-only row naming the source and support, the per-type ledger, and a `Live packet` badge. It is not an editable load — the server resolves it fresh at every compute. The × unlinks it, restoring manual entry.",
    },
    {
      kind: "shot",
      shotId: "link-inherited-row",
      caption: "The inherited row on FJ-1: source, per-type currency, live-packet status, unlink.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "The envelope number is not the transfer",
      body: "A chip's `lb max` figure is the source's display envelope, shown for orientation. What actually transfers is the per-type packet — the picker says so in place: \"The link transfers each load type, correlated case, and fixed-end moment from the authoritative packet.\"",
    },

    { kind: "heading", level: 2, text: "What can't link, and why", id: "refusals" },
    {
      kind: "paragraph",
      text: "Every gate fails closed, and the picker tells you which one fired — the sentence appears on the disabled chip (or on the member row, for cycle blocks). The table below is the complete set, verbatim, in the order the gates run. Angle brackets mark the slots the picker fills in.",
    },
    {
      kind: "table",
      caption: "The picker's refusal sentences and the rule behind each.",
      headers: ["The picker says", "Why"],
      rows: [
        [
          "\"Recompute this source before linking its reactions.\"",
          "Packets are read from computed results only. A stale or never-computed source has nothing authoritative to publish.",
        ],
        [
          "\"This source did not publish an authoritative reaction packet.\"",
          "The calculator produced no packet. When it states its own reason, that sentence prints instead — the concrete beam's is quoted below.",
        ],
        [
          "\"Recompute this source with the current reaction-packet schema.\"",
          "The stored packet predates schema 1.1.0. One recompute regenerates it current.",
        ],
        [
          "\"Line load links require a vertical reaction component.\" (same for Point load)",
          "Line and point landings consume vertical packets. An axial packet has no vertical component to spread along a beam.",
        ],
        [
          "\"Connection demand requires a vertical or axial reaction component.\"",
          "The connection mapping is defined for those two components only.",
        ],
        [
          "\"A tension member requires a vertical hanger/tie reaction.\"",
          "A tie's demand is a vertical hang; an axial source would double-convert.",
        ],
        [
          "\"Unsupported reaction types: ⟨types⟩.\"",
          "The packet carries a load type outside D, L, Lr, S, R, Di, W_down, W_up — nothing downstream knows how to combine it.",
        ],
        [
          "\"Recompute source: scalar and case currency overlap for ⟨types⟩.\"",
          "The packet holds both a scalar and correlated cases for the same type — an inconsistent publish. Recomputing the source rebuilds it cleanly.",
        ],
        [
          "\"⟨calculator⟩ does not price ⟨types⟩ reaction transfer.\"",
          "Restricted targets accept a fixed type set: I-joists and stud walls take D/L/Lr/S/R; tension members add Di and W_down; the biaxial beam-column takes D/L/Lr/S/R/W_down. A type outside the set has no priced path in that calculator.",
        ],
        [
          "\"This one-direction member cannot price a reversed reaction.\"",
          "I-joists, stud walls, and tension members check one direction. A packet with negative currency or a reversing case would need a check that member does not run.",
        ],
        [
          "\"⟨Link type⟩ cannot preserve this source's correlated reaction cases.\"",
          "Correlated cases land only where the target carries a case channel: point/line onto sawn, SCL, and glulam beams (not I-joists), axial onto columns and beam-columns, connection onto the hardware capacity check. The dowel fastening calcs have no case channel, so the api refuses — the picker mirrors it rather than dropping cases silently.",
        ],
        [
          "\"⟨Link type⟩ cannot carry this source's fixed-end moment.\"",
          "Fixed-end moments transfer only point/line onto those same beam calcs, or axial onto the biaxial beam-column. Anywhere else the moment would vanish from the load path.",
        ],
        [
          "\"Non-wind uplift (negative D/L/S) can't be priced by a dowel fastening — use the hardware capacity check.\"",
          "The dowel mapping prices wind uplift (W_up) on the withdrawal path. Negative dead, live, or snow has no defined dowel path; a published hardware row can price it.",
        ],
        [
          "\"NDS tabulates no bolt withdrawal value — price this uplift on nails, screws, lags, or hardware.\"",
          "The NDS publishes no bolt withdrawal capacity, so a bolted sheet cannot accept an uplift packet. This is a code fact, not a product gap.",
        ],
        [
          "\"Axial reaction type ⟨T⟩ can't land as lateral shear on a dowel group.\"",
          "Axial packets map D, L, Lr, and S to lateral shear on the fastener group. Any other type, or a reversal, has nowhere defined to land.",
        ],
        [
          "\"No axial components for the fastening to price.\"",
          "An axial packet whose every component is zero gives the fastening nothing to check.",
        ],
        [
          "\"No per-load-type reaction currency is available.\"",
          "The packet is authoritative but empty — no type carries a value, case, or moment.",
        ],
        [
          "\"would create a circular load path\"",
          "Shown on the member row itself: every member downstream of the target is blocked up front, because linking from it would close a loop in the takedown graph.",
        ],
      ],
    },
    {
      kind: "paragraph",
      text: `A calculator can also refuse at the source. The concrete beam publishes this instead of a packet: "${CONCRETE_REACTION_REFUSAL}" The cold-formed joist shows the opposite case — it publishes unfactored-only rows (D ${CFS_JOIST_REACTIONS.D} lb, L ${CFS_JOIST_REACTIONS.L} lb per end) with no maximum envelope, and the picker simply shows no \`lb max\` figure rather than fabricating one.`,
    },
    {
      kind: "shot",
      shotId: "link-picker-cycle",
      caption: "Search narrowed to the FJ marks while linking onto B-1: FJ-2 offers its packets; FJ-1 is blocked because B-1 already loads it.",
    },

    { kind: "heading", level: 2, text: "Connection links and gravity-only packets", id: "connection-links" },
    {
      kind: "paragraph",
      text: "Dowel fastening sheets (nails, screws, lags, bolts) consume reactions through a fixed orientation mapping, shown for confirmation before the link is made. From a vertical packet: downward types bear on the seat — informational, never fastening demand — and wind uplift `W_up` prices the uplift-withdrawal path under 0.6D+0.6W with no dead-resistance credit. From an axial packet: each of D, L, Lr, S lands as lateral shear on the fastener group. Linking confirms the orientation, and the confirmation prints on the record.",
    },
    {
      kind: "values",
      caption: "The rafter's packet, landing on a nailed connection.",
      rows: [
        { label: `${RAFTER_PACKET.mark} · ${RAFTER_PACKET.support} — D ${grp(RAFTER_PACKET.byType.D)} lb`, value: "bearing seat — informational" },
        { label: `${RAFTER_PACKET.mark} · ${RAFTER_PACKET.support} — S ${grp(RAFTER_PACKET.byType.S)} lb`, value: "bearing seat — informational" },
        { label: `${RAFTER_PACKET.mark} · ${RAFTER_PACKET.support} — W_up ${RAFTER_PACKET.byType.W_up} lb`, value: "uplift withdrawal (0.6D+0.6W)" },
      ],
    },
    {
      kind: "shot",
      shotId: "connection-link-mapping",
      caption: "The mapping confirmation on a nailed connection: per-type destinations, stated before the link exists.",
    },
    {
      kind: "paragraph",
      text: "A gravity-only packet — downward types and no `W_up` — is still linkable onto a dowel sheet, with the fastening demand honestly zero. The sheet records the connection as a detail reference: the rafter still gets nailed to the plate even with no net uplift, and fastening per the schedule or policy still applies. The picker's own words: \"Downward components bear on the seat — no computed fastening demand. This sheet records the connection as a detail reference; fastening per the schedule/policy still applies.\"",
    },

    { kind: "heading", level: 2, text: "Staleness and Recompute all", id: "staleness" },
    {
      kind: "paragraph",
      text: "Recomputing a source whose reaction feeds other sheets flags those sheets **stale** — so does creating or removing a link, and deleting a linked source (the delete dialog names the dependents and offers unlink-all first). Stale is a flag, not an action: no sheet recomputes behind your back.",
    },
    {
      kind: "paragraph",
      text: "The workspace shows the debt in one banner: an `OUT OF DATE` tag and a count — \"2 sheets need recompute after a linked reaction changed.\" — with one button. **Recompute all** refreshes every stale sheet server-side in topological order, upstream before downstream, so a chain of links settles in one pass. A schema sheet that is itself stale says so in place: \"A linked source changed after this result was computed. Recalculate before relying on the checks.\"",
    },
    {
      kind: "shot",
      shotId: "link-stale-banner",
      caption: "The staleness banner: a count of out-of-date sheets and the one action that settles them.",
    },

    { kind: "related", topics: ["your-first-member", "fastening-escalation", "member-loads"] },
  ],
};
